import React, { useState } from "react";
import {
  Card,
  Button,
  Collapse,
  Modal,
  Tooltip,
  OverlayTrigger,
} from "react-bootstrap";
import {
  FaArrowAltCircleDown,
  FaArrowAltCircleRight,
  FaArrowRight,
  FaRegFolderOpen,
  FaBook,
  FaGripLines,
} from "react-icons/fa";
import { MdViewModule } from "react-icons/md";
import "../ModuleAccordion/AccordionItem.css";
import "./ModuleItem.css";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { FaEye } from "react-icons/fa6";
import { FaEyeSlash } from "react-icons/fa6";
import { IoIosRemoveCircle } from "react-icons/io";
import { useNavigate } from "react-router-dom";
import { Boxes } from "lucide-react";
import { useTranslation } from "react-i18next"; // Importer useTranslation

function ModuleItem(props) {
  const { t } = useTranslation(); // Assigner useTranslation à {t}
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({
      id: props.id,
      animateLayoutChanges: () => false,
    });
  const [open, setOpen] = useState(false);
  const [mouseOnModule, setMouseOnModule] = useState(false);
  const [visible, setVisible] = useState(props.is_visible);
  const [modalOpen, setModalOpen] = useState(false);
  const navigate = useNavigate();

  const style = transform
    ? { transition, transform: CSS.Transform.toString(transform) }
    : {};

  let textSizeClass = "";
  if (props.isLesson) {
    textSizeClass = "fs-7";
  } else if (props.isSection) {
    textSizeClass = "fs-6";
  } else {
    textSizeClass = "fs-4";
  }

  let titleStyle = "";
  if (props.isLesson) {
    textSizeClass = "fs-7";
    titleStyle = "text-module";
  } else if (props.isSection) {
    textSizeClass = "fs-6";
  } else {
    textSizeClass = "fs-4";
    titleStyle = "text-module";
  }

  const handleSetVisible = async (newValue) => {
    setVisible(newValue);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}modules/${props.id}`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ is_visible: newValue }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || t("error_occurred"));
      }

      console.log(t("visibility_changed_success"));
    } catch (err) {
      console.error(err.message || t("error_occurred"));
    }
  };

  const renderToolTip = (props, message) => (
    <Tooltip id="button-tooltip" {...props}>
      {message}
    </Tooltip>
  );

  const handleNavigate = () => {
    if (props.isSection) {
      return; // Ne navigue pas si c'est une section
    }

    if (props.isLesson === true) {
      navigate(
        `/programs/${props.program_id}/module/${props.moduleId}/${props.lessonId}`,
        {
          state: {
            comeFromSection: props.sectionId,
            selectedLesson: props.lessonId,
          },
        }
      );
    } else {
      navigate(`/programs/${props.program_id}/module/${props._id}`);
    }
  };

  const handleModalClose = () => setModalOpen(false);

  return (
    <div>
      <Card
        ref={setNodeRef}
        className="mb-2 border-1 rounded-3 shadow card"
        style={style}
      >
        <Card.Header className="d-flex align-items-center bg-light p-3">
          {props.isLesson === true || props.isSection === true || (
            <div
              className="drag-handle me-4"
              {...attributes}
              {...listeners}
              style={{ cursor: "grab" }}
            >
              <FaGripLines size={24} />
            </div>
          )}

          {!props.isLesson && (
            <Button
              className="text-dark fs-5 button-accordion"
              variant="link"
              onClick={() => setOpen(!open)}
            >
              {open ? (
                <FaArrowAltCircleDown className="me-4" />
              ) : (
                <FaArrowAltCircleRight className="me-4" />
              )}
            </Button>
          )}
          {!props.isSection && !props.isLesson && (
            <div
              className="me-3 d-flex align-items-center justify-content-center bg-primary bg-gradient rounded-circle"
              style={{ width: "50px", height: "50px", padding: "10px" }}
            >
              <Boxes size={24} color="#fff" />
            </div>
          )}
          {props.isSection && <FaRegFolderOpen className="me-4" size={30} />}
          {props.isLesson && <FaBook className="me-4" size={20} />}

          <div className="flex-grow-1">
            <h6
              onMouseOver={() => setMouseOnModule(true)}
              onMouseOut={() => setMouseOnModule(false)}
              className={`mb-0 fw-bold ${titleStyle} ${textSizeClass}`}
              onClick={handleNavigate}
            >
              {props.title}{" "}
              {mouseOnModule && !props.isSection && (
                <FaArrowRight className="ms-2" />
              )}
            </h6>
            <p className="text-muted">{props.description}</p>
          </div>

          {props.isLesson === true || props.isSection === true || (
            <>
              <OverlayTrigger
                placement="top"
                overlay={renderToolTip(props, t("remove_module"))}
              >
                <IoIosRemoveCircle
                  onClick={() => setModalOpen(true)}
                  size={30}
                  className="me-2 icon-delete"
                />
              </OverlayTrigger>

              {visible ? (
                <OverlayTrigger
                  placement="top"
                  overlay={renderToolTip(props, t("hide_module"))}
                >
                  <FaEye
                    onClick={() => handleSetVisible(false)}
                    size={30}
                    className="icon"
                  />
                </OverlayTrigger>
              ) : (
                <OverlayTrigger
                  placement="top"
                  overlay={renderToolTip(props, t("show_module"))}
                >
                  <FaEyeSlash
                    onClick={() => handleSetVisible(true)}
                    size={30}
                    className="icon"
                  />
                </OverlayTrigger>
              )}
            </>
          )}
        </Card.Header>
        {!props.isLesson && (
          <Collapse in={open}>
            <Card.Body className="border-top">{props.children}</Card.Body>
          </Collapse>
        )}
      </Card>
      <Modal show={modalOpen} onHide={handleModalClose}>
        <Modal.Header closeButton>
          <Modal.Title>{t("confirm_remove_module")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>{t("module_removal_info")}</Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleModalClose}>
            {t("cancel")}
          </Button>
          <Button
            variant="danger"
            onClick={() => {
              handleModalClose();
              props.handleDelete();
            }}
          >
            {t("remove")}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default ModuleItem;
